import { ChangeEvent, MouseEvent, CSSProperties } from 'react';
import type { Detail } from '../../atoms/recruitmentCheckBox/types';
import {
  OccupationDetailsOccupationId,
  SearchHistories,
  SearchCondition,
  PreferenceConditionCategory,
} from '../../../types/recruitments';

// 「正社員」は雇用期間（なし(正社員)）による検索。WorkingPeriod id=1
export const FULL_TIME_WORKING_PERIOD_ID = '1';
export const FULL_TIME_LABEL = '正社員';

// 給与タイプ（SalaryType マスター準拠: 1=年収, 2=月給, 3=時給）
export const SALARY_TYPES = [
  { id: '1', label: '年収' },
  { id: '2', label: '月給' },
  { id: '3', label: '時給' },
];

// 給与範囲の選択肢（valueは円単位。recruitment_contents.salary_min/max と同じ単位）
// NOTE: app/models/salary_type.rb の SEARCH_RANGE_OPTIONS と一致させること
export const SALARY_RANGE_OPTIONS: { [salaryTypeId: string]: { value: string; label: string }[] } = {
  '1': [100, 200, 250, 300, 350, 400, 450, 500, 600, 700, 800, 900, 1000].map((man) => ({
    value: String(man * 10000),
    label: `${man}万円`,
  })),
  '2': [15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 70, 80, 90, 100].map((man) => ({
    value: String(man * 10000),
    label: `${man}万円`,
  })),
  '3': [1000, 1100, 1200, 1300, 1500, 1800, 2000, 2300, 2500, 3000, 3500, 4000, 4500, 5000].map((yen) => ({
    value: String(yen),
    label: `${yen.toLocaleString()}円`,
  })),
};

export type SalaryChange = {
  target_salary_type?: string;
  target_salary_min?: string;
  target_salary_max?: string;
};

export type SalaryRangeOption = { value: string; label: string };
export type SalaryRangeOptionWithDisabled = SalaryRangeOption & { disabled: boolean };

// 給与範囲セレクトの選択肢に、逆側の値を跨ぐものを disabled として付与する。
// bound='min': 下限セレクト用。上限(otherValue)より大きい額を無効化。
// bound='max': 上限セレクト用。下限(otherValue)より小さい額を無効化。
// 同額は許可（下限は「上限以下」、上限は「下限以上」）。otherValue 未設定なら全て有効。
export const withSalaryRangeDisabled = (
  options: SalaryRangeOption[],
  otherValue: string | undefined,
  bound: 'min' | 'max',
): SalaryRangeOptionWithDisabled[] => {
  const other = otherValue ? Number(otherValue) : null;
  return options.map((option) => {
    if (other === null) return { ...option, disabled: false };
    const value = Number(option.value);
    const disabled = bound === 'min' ? value > other : value < other;
    return { ...option, disabled };
  });
};

export type SalaryType = { id: string; label: string };

// 表示する給与タイプを絞り込む。
// salaryTypeIds（会社/ブランドページで対象求人が持つ給与タイプidの一覧）が渡された場合は
// それに含まれるタイプのみ返す。未指定（求人検索ページ）なら全タイプを返す。
export const filterSalaryTypes = (salaryTypeIds: number[] | undefined): SalaryType[] =>
  salaryTypeIds
    ? SALARY_TYPES.filter((type) => salaryTypeIds.includes(Number(type.id)))
    : SALARY_TYPES;

// サイドバーのこだわり条件に一部抽出で表示する条件名（Figma準拠、マスターの name と一致させること）
export const SIDEBAR_PREFERENCE_CONDITION_NAMES = [
  '未経験OK',
  '土日祝休み',
  '完全週休2日制',
  '年間休日120日以上',
  '賞与・ボーナスあり',
  'インセンティブあり',
  '家賃補助あり',
  '面接2回以下',
  'オンライン面接OK',
];

export type Occupation = {
  id: number;
  name: string;
};

export type OccupationDetail = Occupation & {
  occupation_id: number;
};

export type Prefecture = {
  id: number;
  name: string;
  areaName: string;
};

export type OccupationDetailCollection = {
  occupation_id: number;
  occupation_details: OccupationDetail[];
};

export type Props = {
  shouldTriggerSearch?: boolean;
  isFromRecruitmentPage?: boolean;
  target_employment_types: string[];
  target_occupations: string[];
  target_occupation_details: string[];
  target_prefectures: string[];
  target_prefecture_details: string[];
  q?: string;
  page?: number;
  recruitment_employment_types: string[];
  occupations: Occupation[];
  side_prefectures: Prefecture[];
  all_prefectures: Prefecture[];
  detail_prefectures: { [key: string]: Prefecture[] };
  occupation_details: OccupationDetailCollection[];
  search_occupation_details?: string[];
  occupationDetailsOccupationIds: OccupationDetailsOccupationId[];
  prefectureDetails: { [key: string]: Detail[] };
  isPc: boolean;
  isSp: boolean;
  isPage?: string;
  popupStyle: CSSProperties;
  modalStyle: CSSProperties;
  spStyle: CSSProperties;
  toggleListStyle: CSSProperties;
  recruitmentsCount: number;
  handleClick: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSearch: (type: string) => void;
  handleChangeUrl?: (path: string) => void;
  handleChangeQ: (name: 'q', value: string) => void;
  checkAllOccupationDetails: (value: number | string, checked: boolean, baseArray?: any[]) => void;
  checkAllPrefectureDetails: (baseArray: string[], value: string, checked: boolean) => void;
  handleFetchOccupationDetails: (occupationId: string, occupationDetailId: string) => void;
  handleFetchPrefectureDetails: (areaName: string, prefectureId: string) => void;
  openList: (event: MouseEvent<HTMLLIElement>) => void;
  handlePopup: () => void;
  handleModal: () => void;
  handleOpenSp: () => void;
  closePopup: () => void;
  handleClearAll?: () => void;
  handleClearEmploymentTypes?: () => void;
  handleSelectAllEmploymentTypes?: () => void;
  handleClearOccupations?: () => void;
  handleSelectAllOccupations?: () => void;
  handleClearLocations?: () => void;
  handleSelectAllLocations?: () => void;
  target_preference_conditions?: string[];
  target_working_periods?: string[];
  preference_condition_categories?: PreferenceConditionCategory[];
  // 会社ページのみ: 対象求人が持つ雇用期間idの一覧（「正社員」表示の判定に使用。未指定なら常に表示）
  working_period_ids?: number[];
  // 会社/ブランドページのみ: 対象求人が持つ給与タイプidの一覧（給与タイプ絞り込みに使用。未指定なら全タイプ表示）
  salary_type_ids?: number[];
  handleClearPreferenceConditions?: () => void;
  handleSelectAllPreferenceConditions?: () => void;
  target_salary_type?: string;
  target_salary_min?: string;
  target_salary_max?: string;
  handleChangeSalary?: (change: SalaryChange) => void;
  handleClearAreas?: () => void;
  handleClearPrefectureDetails?: () => void;
  searchHistories?: SearchHistories;
  handleApplySearchHistory?: (condition: SearchCondition) => void;
  handleSaveConditions?: (title: string) => void;
  saveConditions?: SearchCondition[];
  handleDeleteSaveConditions?: (conditionId: number | string) => void;
};
